import { useState, useEffect } from 'react';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const IS_DEVELOPMENT = import.meta.env.DEV;

export const useRecaptcha = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRecaptcha = () => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha) {
        resolve(window.grecaptcha);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        window.grecaptcha.ready(() => {
          resolve(window.grecaptcha);
        });
      };
      
      script.onerror = () => {
        reject(new Error('Error al cargar reCAPTCHA'));
      };

      document.head.appendChild(script);
    });
  };

  const executeRecaptcha = async (action = 'homepage') => {
    try {
      setIsLoading(true);
      setError(null);

      // En desarrollo, simular verificación exitosa sin reCAPTCHA real
      if (IS_DEVELOPMENT) {
        //console.log('🔧 Modo desarrollo: Simulando verificación reCAPTCHA exitosa');
        
        // Simular un pequeño delay para que se vea el loading
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setIsVerified(true);
        setIsLoading(false);
        
        return { success: true, token: 'dev-token-' + Date.now() };
      }

      // Modo producción: usar reCAPTCHA real
      const recaptcha = await loadRecaptcha();
      
      const token = await recaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: action
      });

      // console.log('token', token);
      
      // Aquí normalmente enviarías el token a tu backend para verificación
      const isValid = await verifyToken(token);
      
      setIsVerified(isValid);
      setIsLoading(false);
      
      return { success: isValid, token };
    } catch (err) {
      console.error('❌ Error en reCAPTCHA:', err);
      setError(err.message);
      setIsLoading(false);
      setIsVerified(false);
      return { success: false, error: err.message };
    }
  };

  // Simulación de verificación del token (en producción esto va en tu backend)
  const verifyToken = async (token) => {
    try {
      // En producción, esto sería una llamada a tu API backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}users?verify_captcha_web_v3`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: token })
      });
      const result = await response.json();
      return result.data.success && result.data.score > 0.5;
    } catch (err) {
      console.error('Error verificando reCAPTCHA:', err);
      return false;
    }
  };

  useEffect(() => {
    //console.log('🚀 Iniciando verificación reCAPTCHA...');
    //console.log('🔑 VITE_RECAPTCHA_SITE_KEY:', import.meta.env.VITE_RECAPTCHA_SITE_KEY);
    //console.log('🔑 RECAPTCHA_SITE_KEY (const):', RECAPTCHA_SITE_KEY);
    //console.log('🏗️ IS_DEVELOPMENT:', IS_DEVELOPMENT);
    //console.log('📍 import.meta.env.DEV:', import.meta.env.DEV);
    
    //if (IS_DEVELOPMENT) {
      //console.log('📝 Modo desarrollo detectado - usando simulación');
    //}
    executeRecaptcha('page_load');
  }, []);

  return {
    isVerified,
    isLoading,
    error,
    executeRecaptcha
  };
};
